import { Button } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLocalStorage } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import HorribleImage from 'common/images/horrible.png';
import SuccessImage from 'common/images/success.png';
import { Typography } from 'common/typography';

import { fetchITAEnv } from './utils';
import { sendTokenToTaxService } from '../../apis/tax-reporting-service/api';

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block: 40px;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 24px;
  }
`;

const Message = styled(Typography.Body2)`
  margin-block-start: 16px;
`;

const StyledButton = styled(Button)`
  margin-block-start: 24px;
`;

const StyledLink = styled.a`
  margin-block-start: 24px;
  max-width: max-content;
  color: ${colors.wolt100};
  text-decoration: 'none';
`;

const ScreenCenteredImage = styled.img`
  width: 300px;
  height: 300px;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    width: 150px;
    height: 150px;
  }
`;

export const TaxReporting = () => {
  const [variant, setVariant] = useState<string | null>(null);
  const [message, setMessage] = useState<{ title: string; description: string }>({ title: '', description: '' });
  const history = useHistory();

  const [merchantId, setMerchantId] = useLocalStorage('merchantId', '');

  const { isLoading, mutate } = useMutation(
    ({ code, merchantId }: { code: string; merchantId: string | undefined }) => sendTokenToTaxService(code, merchantId),
    {
      onSuccess: (_, variables, __) => {
        const { merchantId } = variables;
        setMerchantId('');
        setVariant('success');
        setMessage({ title: 'Done! Token saved successfully', description: 'Redirecting back to merchant page' });

        setTimeout(() => {
          history.push(`/merchant/${merchantId}/insights`);
        }, 5000);
      },
      onError: (
        error: AxiosError<{
          error: string;
          error_description: string;
        }>,
      ) => {
        setVariant('error');
        setMessage({
          title: (!!error.response && error.response.data.error) || 'Error',
          description:
            (!!error.response && error.response.data.error_description) || 'An error occurred while saving token',
        });
      },
    },
  );

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) {
      mutate({ code, merchantId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { itaUri, clientId, redirectUri } = fetchITAEnv();

  return (
    <>
      {isLoading && (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {!isLoading &&
        !!variant &&
        (variant === 'success' ? (
          <MessageContainer>
            <ScreenCenteredImage src={SuccessImage} />
            <Typography.Heading3>
              <FormattedMessage id="tax-reporting.state.info.title" />
            </Typography.Heading3>
            <Message>
              <FormattedMessage id="tax-reporting.state.info.message" />
            </Message>
            <StyledButton variant="blue" size="small" onClick={() => history.push(`/merchant/${merchantId}/insights`)}>
              <FormattedMessage id="tax-reporting.state.info.back-button" />
            </StyledButton>
          </MessageContainer>
        ) : (
          <MessageContainer>
            <ScreenCenteredImage src={HorribleImage} />
            <Typography.Heading3>{message.title}</Typography.Heading3>
            <Message>{message.description}</Message>

            {!!merchantId ? (
              <>
                <Message>
                  <FormattedMessage id="tax-reporting.try-again" />
                </Message>
                <StyledLink
                  href={`${itaUri}?response_type=code&client_id=${clientId}&scope=scope&redirect_uri=${redirectUri}`}
                  rel="noreferrer"
                >
                  <Button size="small" variant="blue">
                    <FormattedMessage id="tax-reporting.login-button" />
                  </Button>
                </StyledLink>
              </>
            ) : (
              <StyledButton size="small" variant="blue" onClick={() => history.push('/')}>
                <FormattedMessage id="tax-reporting.state.info.back-button" />
              </StyledButton>
            )}
          </MessageContainer>
        ))}
    </>
  );
};
